import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Loader2, ShoppingBag, MapPin, ExternalLink, Package, Truck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import OrderTimeline, { stageLabel } from "@/components/OrderTimeline";
import ShipDialog from "@/components/ShipDialog";

const Sales = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTx, setBusyTx] = useState(null);
  const [shipFor, setShipFor] = useState(null); // {tx_id, isService} when ship dialog is open
  const { formatPrice } = useCurrency();

  const load = () => {
    setLoading(true);
    api.get("/me/sales")
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Could not load your sales"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStage = async (txId, payload) => {
    setBusyTx(txId);
    try {
      await api.post(`/transactions/${txId}/fulfilment`, payload);
      toast.success(`Order marked as ${payload.fulfilment_status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusyTx(null); }
  };

  return (
    <div className="container-narrow py-10" data-testid="sales-page">
      <div className="mb-8">
        <div className="label-overline text-[#C05A3C] mb-2">Seller</div>
        <h1 className="font-display text-4xl tracking-tight">Your Sales</h1>
        <p className="text-muted-foreground mt-2 font-sans-ui">
          Manage fulfilment for orders buyers have paid for. Update the status so they know what&apos;s next.
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center bg-white border border-black/10 rounded-lg" data-testid="sales-empty">
          <ShoppingBag className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-display text-2xl">No sales yet</h3>
          <p className="text-muted-foreground mt-2">When buyers purchase your listings, orders show up here.</p>
          <Link to="/dashboard" className="mt-5 inline-block text-[#C05A3C] hover:underline font-semibold">
            Back to dashboard →
          </Link>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => {
            const img = o.listing?.images?.[0];
            const failed = o.status === "failed" || o.status === "expired";
            const txId = o.transaction_id || o.session_id;
            const busy = busyTx === txId;
            const canPack = o.stage === "preparing";
            const canShip = o.stage === "preparing" || o.stage === "packed";
            const canDeliver = o.stage === "shipped";
            return (
              <div
                key={txId}
                className="bg-white border border-black/10 rounded-lg overflow-hidden"
                data-testid={`sale-row-${txId}`}
              >
                <div className="flex flex-col sm:flex-row gap-4 p-5 border-b border-black/5">
                  {img && (
                    <Link to={`/listing/${o.listing_id}`} className="shrink-0">
                      <img src={img} alt={o.listing?.title || "Sale"} className="w-full sm:w-28 h-28 object-cover rounded-md" />
                    </Link>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <Link to={`/listing/${o.listing_id}`} className="font-display text-xl leading-tight hover:text-[#C05A3C] transition-colors">
                          {o.listing?.title || "Listing"}
                        </Link>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          {o.listing?.city && <><MapPin className="w-3 h-3" />{o.listing.city}, {o.listing.country}</>}
                          {o.buyer?.name && <> · bought by <span className="font-semibold">{o.buyer.name}</span>{o.buyer.country ? ` (${o.buyer.country})` : ""}</>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl text-[#C05A3C]">
                          {formatPrice(o.amount, o.currency) || `${o.currency || ""} ${o.amount}`}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                          {failed ? "Payment failed" : stageLabel(o.stage)}
                        </div>
                        {!failed && o.payout_status && (
                          <div className="mt-1 text-[11px] font-medium" data-testid={`payout-status-${txId}`}>
                            {o.payout_status === "released" ? (
                              <span className="text-[#3E7D57]">✓ Payout released</span>
                            ) : o.payout_status === "pending_manual" ? (
                              <span className="text-[#3E7D57]">✓ Payout due (manual)</span>
                            ) : o.payout_status === "refunded" ? (
                              <span className="text-[#B01E28]">Refunded to buyer</span>
                            ) : o.payout_status === "held" ? (
                              <span className="text-[#B57A1E]">
                                Payout held{o.payout_hold_until ? ` · releases ${new Date(o.payout_hold_until).toLocaleDateString()}` : ""}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Awaiting payment</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-5 sm:py-6">
                  {failed ? (
                    <div className="text-sm text-[#C05A3C] bg-[#C05A3C]/10 rounded-md p-3">
                      The buyer&apos;s payment did not complete. No action needed.
                    </div>
                  ) : (
                    <OrderTimeline
                      stage={o.stage}
                      shippedAt={o.shipped_at}
                      deliveredAt={o.delivered_at}
                      trackingUrl={o.tracking_url}
                    />
                  )}
                  {(o.tracking_number || o.tracking_url) && (
                    <div className="mt-4 bg-[#F3EFE9] rounded-md px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
                      {o.tracking_carrier && <span className="font-semibold">{o.tracking_carrier}</span>}
                      {o.tracking_number && <span className="font-mono">{o.tracking_number}</span>}
                      {o.tracking_url && (
                        <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C05A3C] hover:underline font-semibold ml-auto">
                          <ExternalLink className="w-3 h-3" /> View tracking
                        </a>
                      )}
                    </div>
                  )}
                  {/* Action buttons — only on paid, non-final orders */}
                  {!failed && o.stage !== "delivered" && (
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-black/5 pt-4">
                      {canPack && (
                        <Button
                          data-testid={`mark-packed-btn-${txId}`}
                          onClick={() => updateStage(txId, { fulfilment_status: "packed" })}
                          disabled={busy}
                          variant="outline"
                          className="border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white"
                        >
                          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Package className="w-4 h-4 mr-2"/>}
                          Mark as packed
                        </Button>
                      )}
                      {canShip && (
                        <Button
                          data-testid={`mark-shipped-btn-${txId}`}
                          onClick={() => setShipFor({ txId, isService: o.listing?.type === "service" })}
                          disabled={busy}
                          className="bg-[#C05A3C] hover:bg-[#A8492F] text-white"
                        >
                          <Truck className="w-4 h-4 mr-2"/> Mark as shipped
                        </Button>
                      )}
                      {canDeliver && (
                        <Button
                          data-testid={`mark-delivered-btn-${txId}`}
                          onClick={() => updateStage(txId, { fulfilment_status: "delivered" })}
                          disabled={busy}
                          variant="outline"
                          className="border-[#2A3D36] text-[#2A3D36] hover:bg-[#2A3D36] hover:text-white"
                        >
                          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <CheckCircle2 className="w-4 h-4 mr-2"/>}
                          Mark as delivered
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Buyer-uploaded proof of delivery */}
                  {o.delivery_proof_url && (
                    <div className="mt-4 border-t border-black/5 pt-4" data-testid="sale-proof-display">
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#2A3D36] mb-2 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Delivery confirmed by buyer
                      </div>
                      <img src={o.delivery_proof_url} alt="Buyer proof of delivery" className="rounded-md max-h-44 object-cover" />
                      {o.delivery_proof_note && (
                        <div className="text-xs text-muted-foreground mt-2 italic">&ldquo;{o.delivery_proof_note}&rdquo;</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ShipDialog
        open={!!shipFor}
        onClose={() => setShipFor(null)}
        busy={!!busyTx}
        isService={!!shipFor?.isService}
        onConfirm={async (payload) => {
          await updateStage(shipFor.txId, { fulfilment_status: "shipped", ...payload });
          setShipFor(null);
        }}
      />
    </div>
  );
};

export default Sales;
